import { Controller } from '@hotwired/stimulus'

export default class HelperController extends Controller {
  static targets = ['emojiShow']

  initialize() {
    this.handleEmojiCopy = this.handleEmojiCopy.bind(this)
    this.handleEmojiSelection = this.handleEmojiSelection.bind(this)
    this.selectedEmojis = new Set()
    this.selectionFrame = null
  }

  connect() {
    document.addEventListener('selectionchange', this.handleEmojiSelection)
    this.checkPostScrolling()
  }

  disconnect() {
    document.removeEventListener('selectionchange', this.handleEmojiSelection)
    if (this.selectionFrame) cancelAnimationFrame(this.selectionFrame)
    this.selectionFrame = null
    this.clearEmojiSelection()
  }

  emojiShowTargetConnected(element) {
    for (const emoji of element.querySelectorAll('.ql-emoji')) {
      const url = emoji.dataset.url
      if (!url) continue

      emoji.style.backgroundImage = `url("${url}")`
      emoji.style.backgroundRepeat = 'no-repeat'
      emoji.style.position = 'relative'
    }

    element.addEventListener('copy', this.handleEmojiCopy)
  }

  emojiShowTargetDisconnected(element) {
    element.removeEventListener('copy', this.handleEmojiCopy)
  }

  handleEmojiCopy(event) {
    const selection = globalThis.getSelection()
    if (!selection || selection.rangeCount === 0 || !event.clipboardData) return

    const fragment = selection.getRangeAt(0).cloneContents()
    const emojis = fragment.querySelectorAll?.('.ql-emoji') || []
    if (emojis.length === 0) return

    for (const emoji of emojis) {
      const nativeEmoji = emoji.dataset.native || this.nativeEmoji(emoji.dataset.emoji)
      emoji.replaceWith(document.createTextNode(nativeEmoji))
    }

    const container = document.createElement('div')
    container.append(fragment)

    event.preventDefault()
    event.clipboardData.setData('text/plain', this.plainText(container))
    event.clipboardData.setData('text/html', container.innerHTML)
  }

  handleEmojiSelection() {
    if (this.selectionFrame) return

    this.selectionFrame = requestAnimationFrame(() => {
      this.selectionFrame = null
      this.updateEmojiSelection()
    })
  }

  updateEmojiSelection() {
    const selection = globalThis.getSelection()
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null
    const nextSelectedEmojis = new Set()

    if (!range || selection.isCollapsed) {
      this.clearEmojiSelection()
      return
    }

    for (const element of this.selectionContainers(selection, range)) {
      for (const emoji of element.querySelectorAll('.ql-emoji')) {
        try {
          if (range.intersectsNode(emoji)) nextSelectedEmojis.add(emoji)
        } catch (_error) {
          // Ignore nodes removed by a Turbo render during selection.
        }
      }
    }

    for (const emoji of this.selectedEmojis) {
      if (!nextSelectedEmojis.has(emoji)) emoji.classList.remove('emoji-selected')
    }

    for (const emoji of nextSelectedEmojis) emoji.classList.add('emoji-selected')
    this.selectedEmojis = nextSelectedEmojis
  }

  selectionContainers(selection, range) {
    const selector = '[data-helper-target~="emojiShow"]'
    const start = selection.anchorNode?.parentElement?.closest(selector)
    const finish = selection.focusNode?.parentElement?.closest(selector)

    if (start && start === finish) return [start]

    return this.emojiShowTargets.filter((element) => {
      try {
        return range.intersectsNode(element)
      } catch (_error) {
        return false
      }
    })
  }

  clearEmojiSelection() {
    for (const emoji of this.selectedEmojis) emoji.classList.remove('emoji-selected')
    this.selectedEmojis.clear()
  }

  nativeEmoji(unified) {
    if (!unified) return ''

    try {
      const codepoints = unified.split('-').map((value) => Number.parseInt(value, 16))
      return String.fromCodePoint(...codepoints)
    } catch (_error) {
      return ''
    }
  }

  plainText(container) {
    for (const br of container.querySelectorAll('br')) {
      br.replaceWith(document.createTextNode('\n'))
    }

    for (const block of container.querySelectorAll('p, div, li')) {
      if (!block.textContent.endsWith('\n')) block.append(document.createTextNode('\n'))
    }

    return container.textContent.replace(/\n+$/g, '')
  }

  checkPostScrolling() {
    const match = window.location.pathname.match(/^\/posts\/([^/]+)$/)

    if (!match) return

    const postId = match[1]
    const post = document.getElementById(postId)

    if (!post) return

    requestAnimationFrame(() => {
      post.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    })
  }
}
